// src/views/admin/profile/components/Banner.tsx

import Card from "components/card";
import defaultAvatar from "assets/img/avatars/avatar4.png";
import defaultBanner from "assets/img/profile/banner.png";
import { db } from "services/firebase";
import { useAuth } from "context/AuthContext";
import { useDevices } from "context/DeviceContext";
import { useUnidades } from "context/UnidadeContext";
import { useFuncionarios } from "context/FuncionarioContext";
import { MdModeEditOutline } from "react-icons/md";
import { uploadProfileImage } from "services/storage";
import { useState, useEffect } from "react";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";

export default function Banner() {
  const { user } = useAuth();
  const { unidades } = useUnidades();
  const { devices } = useDevices();
  const { funcionarios } = useFuncionarios();
  const [avatarSrc, setAvatarSrc] = useState(defaultAvatar);
  const [bannerSrc, setBannerSrc] = useState(defaultBanner);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);

  const getProfileDocRef = () => {
    if (!user?.uid) return null;
    return doc(db, `clientes/${user.uid}/perfil`, "data");
  };

  useEffect(() => {
    if (user) {
      const fetchImages = async () => {
        try {
          const userRef = getProfileDocRef();
          if (!userRef) return;

          const docSnap = await getDoc(userRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.avatarUrl) setAvatarSrc(data.avatarUrl);
            if (data.bannerUrl) setBannerSrc(data.bannerUrl);
          }
        } catch (error) {
          console.error("Erro ao buscar imagens do perfil:", error);
        }
      };
      fetchImages();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "avatar" | "banner"
  ) => {
    const file = e.target.files?.[0];
    if (!file || !user?.uid) return;

    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('O arquivo deve ter no máximo 5MB.');
      return;
    }

    const setIsUploading = type === 'avatar' ? setIsUploadingAvatar : setIsUploadingBanner;
    const setSrc = type === 'avatar' ? setAvatarSrc : setBannerSrc;
    const defaultSrc = type === 'avatar' ? defaultAvatar : defaultBanner;

    setIsUploading(true);
    
    try {
      const previewUrl = URL.createObjectURL(file);
      setSrc(previewUrl);

      const url = await uploadProfileImage(file, user.uid, type);
      
      const userRef = getProfileDocRef();
      if (!userRef) throw new Error("Referência do perfil não encontrada");

      const docSnap = await getDoc(userRef);
      const dataToUpdate = type === 'avatar' ? { avatarUrl: url } : { bannerUrl: url };

      if (docSnap.exists()) {
        await updateDoc(userRef, dataToUpdate);
      } else {
        await setDoc(userRef, dataToUpdate);
      }
      
      setSrc(url);
      URL.revokeObjectURL(previewUrl);
    } catch (error) {
      console.error(`Erro ao fazer upload do ${type}:`, error);
      alert(`Erro ao fazer upload da imagem. Tente novamente.`);
      setSrc(defaultSrc);
    } finally {
      setIsUploading(false);
    }
  };

  const email = user?.email?.split("@")[0] || "Usuário";
  const unidadesCount = unidades.length;
  const dispositivosCount = devices.length;
  const funcionariosCount = funcionarios.length;

  return (
    <Card extra="items-center w-full h-full p-[16px] bg-cover">
      <div className="relative mt-1 flex h-32 w-full justify-center rounded-xl bg-cover" style={{ backgroundImage: `url(${bannerSrc})` }}>
        <label className={`absolute top-2 right-2 cursor-pointer text-white bg-[#a3aed0] rounded-full p-1 ${isUploadingBanner ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <MdModeEditOutline size={18} />
          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageChange(e, 'banner')} disabled={isUploadingBanner} />
        </label>
        {isUploadingBanner && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl">
            <div className="text-white text-sm">Carregando...</div>
          </div>
        )}
        <div className="absolute -bottom-12 flex h-[87px] w-[87px] items-center justify-center rounded-full border-[4px] border-white bg-pink-400">
          <img className="h-full w-full rounded-full" src={avatarSrc} alt="Avatar" />
          <label className={`absolute right-0 bottom-0 cursor-pointer text-white bg-[#a3aed0] rounded-full p-1 ${isUploadingAvatar ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <MdModeEditOutline size={18} />
            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageChange(e, 'avatar')} disabled={isUploadingAvatar} />
          </label>
          {isUploadingAvatar && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
              <div className="text-white text-xs">...</div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-16 flex flex-col items-center">
        <h4 className="text-xl font-bold text-navy-700">{email}</h4>
        <p className="text-base font-normal text-gray-600">Administrador</p>
      </div>

      <div className="mt-6 mb-3 flex gap-4 md:!gap-10">
        <Stat label="Unidades" value={unidadesCount} />
        <Stat label="Dispositivos" value={dispositivosCount} />
        <Stat label="Funcionários" value={funcionariosCount} />
      </div>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex flex-col items-center justify-center">
      <p className="text-2xl font-bold text-navy-700">{value}</p>
      <p className="text-sm font-normal text-gray-600">{label}</p>
    </div>
  );
}
